import React, { useState } from "react";
import Typography from "@mui/material/Typography";
import { useParams, useNavigate } from "react-router-dom";
import {
  useAddGameFeedback,
  useAddPlayTestFeedback,
  usePublicGame,
  usePublicProfile,
} from "@seabrookstudios/pitch2table";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import { FeedbackGameInfo } from "./FeedbackGameInfo";
import { PublicSiteFrame } from "./PublicSiteFrame";
import { useSnackbar } from "notistack";
import { Path } from "../Path";
import { EmptyDetailCardWithLogin } from "../core/EmptyDetailCard";

const MAX_CHARACTERS = 500;

/**
 * @param {object} props
 * @param {string} props.username
 * @param {string} props.slug
 * @param {function(string): Promise<void>} props.onSendFeedback
 */
const FeedbackForm = ({ username, slug, onSendFeedback }) => {
  const { data: profile, isLoading } = usePublicProfile(username);
  const { data: game, isLoading: isLoadingGame } = usePublicGame(username, slug);
  const { enqueueSnackbar } = useSnackbar();

  const [feedback, setFeedback] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!profile || isLoading || isLoadingGame || !game) {
    return (
      <PublicSiteFrame>
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      </PublicSiteFrame>
    );
  }

  if (!profile.isPublic) {
    return (
      <PublicSiteFrame>
        <EmptyDetailCardWithLogin text="This designer profile is private or no longer accessible. Please contact the designer directly." />
      </PublicSiteFrame>
    );
  }

  // if (game.visibility === "Private") {
  //   return (
  //     <PublicSiteFrame>
  //       <EmptyDetailCardWithLogin text="This game is private or no longer accessible. Please contact the designer directly." />
  //     </PublicSiteFrame>
  //   );
  // }

  if (!game.feedbackEnabled) {
    return (
      <PublicSiteFrame>
        <EmptyDetailCardWithLogin text="Feedback is not enabled for this game. Please contact the designer directly." />
      </PublicSiteFrame>
    );
  }

  const handleFeedbackChange = (event) => {
    const value = event.target.value;
    if (value.length <= MAX_CHARACTERS) {
      setFeedback(value);
    }
  };

  /**
   * @param {React.FormEvent<HTMLFormElement>} event
   */
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!feedback.trim()) {
      enqueueSnackbar("No feedback found", { variant: "error" });
      return;
    }

    setIsSubmitting(true);

    try {
      await onSendFeedback(feedback.trim());
    } catch (error) {
      console.error("Failed to submit feedback:", error);
      enqueueSnackbar("Failed to submit feedback. Please try again.", { variant: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const charactersRemaining = MAX_CHARACTERS - feedback.length;

  return (
    <PublicSiteFrame>
      <Card>
        <Box justifyContent="center" display="flex">
          <Box maxWidth="66ch">
            <Box sx={{ padding: { xs: 2, sm: 2, md: 4, lg: 4 } }}>
              <Typography align="center" variant="h3" gutterBottom>
                Send Play Test Feedback
              </Typography>

              {game.visibility === "Public" && <FeedbackGameInfo game={game} />}

              <form onSubmit={handleSubmit}>
                <Stack spacing={3}>
                  <TextField
                    label="Your Feedback"
                    placeholder="Please share your thoughts about this game..."
                    multiline
                    rows={6}
                    value={feedback}
                    onChange={handleFeedbackChange}
                    fullWidth
                    required
                    helperText={`${charactersRemaining} characters remaining`}
                    error={charactersRemaining < 0}
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={isSubmitting || !feedback.trim() || charactersRemaining < 0}
                    fullWidth
                  >
                    {isSubmitting ? "Submitting..." : "Submit Feedback"}
                  </Button>
                </Stack>
              </form>
            </Box>
          </Box>
        </Box>
      </Card>
    </PublicSiteFrame>
  );
};

const FormWithGeneralFeedback = ({ username, slug }) => {
  const navigate = useNavigate();

  const redirectOnComplete = () => {
    navigate(Path.public().feedbackThankYou(username, slug));
  };

  const addFeedback = useAddGameFeedback(username, slug, redirectOnComplete);

  const onSendFeedback = async (feedback) => {
    await addFeedback.mutateAsync({ message: feedback.trim() });
  };

  return <FeedbackForm username={username} slug={slug} onSendFeedback={onSendFeedback} />;
};

const FormWithPlayTestFeedback = ({ username, slug, playTestId }) => {
  const navigate = useNavigate();

  const redirectOnComplete = () => {
    navigate(Path.public().feedbackThankYou(username, slug));
  };

  const addFeedback = useAddPlayTestFeedback(playTestId, redirectOnComplete);

  const onSendFeedback = async (feedback) => {
    await addFeedback.mutateAsync({ message: feedback.trim() });
  };

  return <FeedbackForm username={username} slug={slug} onSendFeedback={onSendFeedback} />;
};

export const FeedbackFormFromRoute = () => {
  const username = useParams().username;
  const slug = useParams().slug;

  if (!username || !slug) {
    return (
      <PublicSiteFrame>
        <EmptyDetailCardWithLogin text="This game is private or no longer accessible. Please contact the designer directly." />
      </PublicSiteFrame>
    );
  }

  return <FormWithGeneralFeedback username={username} slug={slug} />;
};

export const PlayTestFeedbackFromRoute = () => {
  const username = useParams().username;
  const slug = useParams().slug;
  const playTestId = useParams().playTestId;

  if (!username || !slug || !playTestId) {
    return (
      <PublicSiteFrame>
        <EmptyDetailCardWithLogin text="This game is private or no longer accessible. Please contact the designer directly." />
      </PublicSiteFrame>
    );
  }

  return <FormWithPlayTestFeedback username={username} slug={slug} playTestId={playTestId} />;
};
